import React, { useMemo, useState } from "react";
import { ArrowDownRight, ArrowUpRight, ArrowUpDown, Inbox } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn, formatINR } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

/** ₹ amount, compacted to K / L / Cr for tiles and chart labels */
export function fmtMoney(value: number, compact = true): string {
  const v = Number(value) || 0;
  if (!compact) return formatINR(v);
  const abs = Math.abs(v);
  if (abs >= 1e7) return `₹${(v / 1e7).toFixed(abs >= 1e8 ? 0 : 1)}Cr`;
  if (abs >= 1e5) return `₹${(v / 1e5).toFixed(abs >= 1e6 ? 0 : 1)}L`;
  if (abs >= 1e3) return `₹${(v / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}K`;
  return formatINR(Math.round(v));
}

export const fmtPct = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(v % 1 === 0 ? 0 : 1)}%`);
export const fmtNum = (v: number) => (Number(v) || 0).toLocaleString("en-IN");

export function fmtDate(v?: string | null): string {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export function fmtMonth(period: string): string {
  const [y = 1970, m = 1] = period.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "short" });
}

/** Trigger a browser download of CSV text */
export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Layout pieces
// ---------------------------------------------------------------------------

export function SectionCard({
  title,
  description,
  action,
  children,
  className,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn("bg-white border border-slate-200 shadow-xs gap-4 py-5", className)}>
      <CardHeader className="px-5 flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1 min-w-0">
          <CardTitle className="text-sm font-bold text-slate-900">{title}</CardTitle>
          {description && <CardDescription className="text-xs text-slate-600 font-medium">{description}</CardDescription>}
        </div>
        {action}
      </CardHeader>
      <CardContent className="px-5">{children}</CardContent>
    </Card>
  );
}

export function EmptyState({ message = "No data for this period." }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-xs text-slate-500 font-medium">
      <Inbox className="size-5" />
      <span>{message}</span>
    </div>
  );
}

/** KPI tile: headline value with an optional comparison against the previous period */
export function StatTile({
  label,
  value,
  previous,
  previousLabel = "vs last month",
  hint,
  icon,
  format = "number",
  rawValue,
}: {
  label: string;
  value: string;
  /** Previous-period raw value; pass with rawValue to show a change */
  previous?: number;
  previousLabel?: string;
  rawValue?: number;
  hint?: React.ReactNode;
  icon?: React.ReactNode;
  format?: "number" | "pct";
}) {
  let delta: React.ReactNode = null;
  if (previous !== undefined && rawValue !== undefined) {
    const diff = rawValue - previous;
    const up = diff >= 0;
    const text =
      format === "pct"
        ? `${up ? "+" : ""}${diff.toFixed(1)} pts`
        : previous === 0
          ? rawValue === 0 ? "no change" : "new"
          : `${up ? "+" : ""}${((diff / Math.abs(previous)) * 100).toFixed(0)}%`;
    delta = (
      <span className="inline-flex items-center gap-0.5 text-slate-700 font-semibold">
        {up ? <ArrowUpRight className="size-3.5 text-emerald-600" aria-hidden /> : <ArrowDownRight className="size-3.5 text-rose-600" aria-hidden />}
        {text}
      </span>
    );
  }

  return (
    <Card className="bg-white border border-slate-200 shadow-xs hover:border-slate-300 hover:shadow-sm transition-all gap-2 py-4">
      <CardHeader className="px-4 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{label}</CardTitle>
        {icon && <div className="size-7 rounded-lg bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center shrink-0">{icon}</div>}
      </CardHeader>
      <CardContent className="px-4 space-y-1">
        <div className="text-2xl font-black tabular-nums text-slate-900">{value}</div>
        {(delta || hint) && (
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-600 font-medium">
            {delta}
            {delta && <span>{previousLabel}</span>}
            {hint}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Target progress: filled bar plus the achieved % as text (never color alone) */
export function TargetBar({ achieved, target, compact }: { achieved: number; target: number; compact?: boolean }) {
  if (!target) {
    return <span className="text-xs text-muted-foreground">No target set</span>;
  }
  const pctValue = (achieved / target) * 100;
  return (
    <div className={cn("space-y-1 w-full", compact ? "min-w-[120px]" : "min-w-0")}>
      <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--viz-track)" }}>
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.min(100, pctValue)}%`, background: "var(--viz-series-1)" }}
        />
      </div>
      <div className="flex justify-between text-[11px] text-muted-foreground tabular-nums">
        <span className="font-semibold text-foreground pr-2">{pctValue.toFixed(0)}%</span>
        <span>
          {fmtMoney(achieved)} / {fmtMoney(target)}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Charts (single series → one hue, no legend; values labelled, tooltip on hover/focus)
// ---------------------------------------------------------------------------

interface ChartTooltip {
  x: number;
  y: number;
  title: string;
  lines: string[];
}

function TooltipBox({ tip }: { tip: ChartTooltip | null }) {
  if (!tip) return null;
  return (
    <div
      role="status"
      className="pointer-events-none absolute z-20 rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md -translate-x-1/2 -translate-y-full"
      style={{ left: tip.x, top: tip.y - 8 }}
    >
      {tip.lines.map((l, i) => (
        <div key={i} className={i === 0 ? "font-semibold text-foreground tabular-nums" : "text-muted-foreground tabular-nums"}>
          {l}
        </div>
      ))}
      <div className="text-muted-foreground">{tip.title}</div>
    </div>
  );
}

export interface BarDatum {
  label: string;
  value: number;
  /** Extra tooltip lines, e.g. "12 deals" */
  detail?: string[];
}

/** Horizontal bar list: label left, bar from a shared baseline, value at the tip */
export function BarList({
  data,
  format = fmtNum,
  emptyMessage,
}: {
  data: BarDatum[];
  format?: (v: number) => string;
  emptyMessage?: string;
}) {
  const [tip, setTip] = useState<ChartTooltip | null>(null);
  const max = Math.max(0, ...data.map((d) => d.value));
  if (data.length === 0 || max === 0) return <EmptyState message={emptyMessage} />;

  const show = (e: React.SyntheticEvent<HTMLElement>, d: BarDatum) => {
    const el = e.currentTarget;
    const parent = el.closest("[data-chart-root]") as HTMLElement;
    const r = el.getBoundingClientRect();
    const pr = parent.getBoundingClientRect();
    setTip({ x: r.left - pr.left + r.width / 2, y: r.top - pr.top, title: d.label, lines: [format(d.value), ...(d.detail || [])] });
  };

  return (
    <div className="relative space-y-2" data-chart-root onMouseLeave={() => setTip(null)}>
      {data.map((d) => (
        <div
          key={d.label}
          tabIndex={0}
          className="group grid grid-cols-[minmax(80px,30%)_1fr] items-center gap-3 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring py-0.5"
          onMouseEnter={(e) => show(e, d)}
          onFocus={(e) => show(e, d)}
          onBlur={() => setTip(null)}
          aria-label={`${d.label}: ${format(d.value)}`}
        >
          <span className="truncate text-xs text-muted-foreground" title={d.label}>
            {d.label}
          </span>
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex-1 min-w-0">
              <div
                className="h-4 rounded-r-[4px] transition-colors group-hover:[background:var(--viz-series-1-hover)]"
                style={{ width: `${Math.max(1.5, (d.value / max) * 100)}%`, background: "var(--viz-series-1)" }}
              />
            </div>
            <span className="text-xs font-semibold tabular-nums text-foreground w-16 text-right shrink-0">{format(d.value)}</span>
          </div>
        </div>
      ))}
      <TooltipBox tip={tip} />
    </div>
  );
}

/** Vertical columns over time (e.g. monthly revenue); labels the latest and the peak column */
export function ColumnChart({
  data,
  format = fmtNum,
  height = 160,
  emptyMessage,
}: {
  data: BarDatum[];
  format?: (v: number) => string;
  height?: number;
  emptyMessage?: string;
}) {
  const [tip, setTip] = useState<ChartTooltip | null>(null);
  const max = Math.max(0, ...data.map((d) => d.value));
  if (data.length === 0 || max === 0) return <EmptyState message={emptyMessage} />;
  const peak = data.findIndex((d) => d.value === max);
  const last = data.length - 1;

  const show = (e: React.SyntheticEvent<HTMLElement>, d: BarDatum) => {
    const el = e.currentTarget;
    const parent = el.closest("[data-chart-root]") as HTMLElement;
    const r = el.getBoundingClientRect();
    const pr = parent.getBoundingClientRect();
    const bar = el.querySelector("[data-bar]") as HTMLElement | null;
    const top = bar ? bar.getBoundingClientRect().top : r.top;
    setTip({ x: r.left - pr.left + r.width / 2, y: top - pr.top, title: d.label, lines: [format(d.value), ...(d.detail || [])] });
  };

  return (
    <div className="relative" data-chart-root onMouseLeave={() => setTip(null)}>
      <div className="flex items-end gap-[2px] border-b border-border" style={{ height }}>
        {data.map((d, i) => {
          const h = (d.value / max) * (height - 20);
          const labelled = i === peak || i === last;
          return (
            <div
              key={d.label + i}
              tabIndex={0}
              className="group relative flex-1 h-full flex flex-col items-center justify-end outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
              onMouseEnter={(e) => show(e, d)}
              onFocus={(e) => show(e, d)}
              onBlur={() => setTip(null)}
              aria-label={`${d.label}: ${format(d.value)}`}
            >
              {labelled && d.value > 0 && (
                <span className="text-[10px] font-semibold tabular-nums text-foreground mb-0.5 whitespace-nowrap">{format(d.value)}</span>
              )}
              <div
                data-bar
                className="w-full max-w-[24px] rounded-t-[4px] transition-colors group-hover:[background:var(--viz-series-1-hover)]"
                style={{ height: Math.max(d.value > 0 ? 2 : 0, h), background: "var(--viz-series-1)" }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-[2px] mt-1.5">
        {data.map((d, i) => (
          <span key={d.label + i} className="flex-1 text-center text-[10px] text-muted-foreground truncate">
            {d.label}
          </span>
        ))}
      </div>
      <TooltipBox tip={tip} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sortable data table
// ---------------------------------------------------------------------------

export interface Column<T> {
  key: string;
  header: string;
  /** Value used for sorting; defaults to row[key] */
  sortValue?: (row: T) => number | string;
  render?: (row: T) => React.ReactNode;
  align?: "left" | "right";
  className?: string;
}

export function DataTable<T extends Record<string, any>>({
  columns,
  rows,
  rowKey,
  onRowClick,
  emptyMessage,
  defaultSort,
  footer,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  defaultSort?: { key: string; dir: "asc" | "desc" };
  footer?: React.ReactNode;
}) {
  const [sort, setSort] = useState(defaultSort || null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    const value = (r: T) => (col?.sortValue ? col.sortValue(r) : r[sort.key]);
    return [...rows].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va ?? "").localeCompare(String(vb ?? ""));
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [rows, sort, columns]);

  if (rows.length === 0) return <EmptyState message={emptyMessage} />;

  return (
    <div className="overflow-x-auto -mx-5 px-5">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border text-muted-foreground">
            {columns.map((c) => (
              <th
                key={c.key}
                className={cn("py-2 px-2 font-semibold whitespace-nowrap", c.align === "right" ? "text-right" : "text-left", c.className)}
              >
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground"
                  onClick={() =>
                    setSort((s) => ({ key: c.key, dir: s?.key === c.key && s.dir === "desc" ? "asc" : "desc" }))
                  }
                >
                  {c.header}
                  <ArrowUpDown className={cn("size-3", sort?.key === c.key ? "opacity-100" : "opacity-30")} aria-hidden />
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr
              key={rowKey(r)}
              className={cn("border-b border-border/60 last:border-0", onRowClick && "cursor-pointer hover:bg-accent/60")}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={cn("py-2 px-2 whitespace-nowrap tabular-nums", c.align === "right" ? "text-right" : "text-left", c.className)}
                >
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer}
      </table>
    </div>
  );
}
